import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {OverlayPanel} from "primereact/overlaypanel";
import {Toast} from "primereact/toast";
import {Tooltip} from "primereact/tooltip";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {selectByLanguage} from "@reva/shared/i18n";
import {splitMultilineString} from "@reva/shared/string-utils";

import {AvmDocumentationLink} from "../../component/AvmDocumentationLink";
import AppreciationInputText from "../../component/appreciation/AppreciationInputText";
import {useValueExplanationsVisible} from "../../component/appreciation/appreciationHooks";
import CollapsibleTitleButton from "../../component/CollapsibleTitleButton";
import {InputGrossFloorAreaEditPanel} from "../../component/paramInput/InputGrossFloorArea";
import ParameterInput from "../../component/paramInput/ParameterInput";
import ValueLendingValueInfo from "../../component/ValueLendingValueInfo";
import {useLanguage, useRevaTranslation, useSelectByCurrentLanguage} from "../../hooks/languageHooks";
import {useCheckPermission} from "../../hooks/permissionHooks";
import {setEditedValueParamErrors} from "../../redux/objectParametersSlice";
import {fetchIndicationDocumentation} from "../../redux/pdfThunk";
import {selectPersistenceErrorForSingleParam} from "../../redux/persistenceSelectors";
import {
    selectComparableValueSpecComparableDetailsParameter,
    selectRangesForApiParamCurried,
} from "../../redux/specSelectors";
import {selectSpec} from "../../redux/specSlice";
import {
    initEndpointExpandedGroups,
    selectExpandedGroupsByEndpoint,
    setEditedValueDataGroup,
    toggleExpandedGroup,
} from "../../redux/valueIndicationSlice";
import {
    COMPARABLE_ACCURACY_PROPERTY,
    COMPARABLE_DISTANCE_PROPERTY,
    formatComparableDistance,
} from "../../util/comparableDistance";
import {aggregateIndicationTableData} from "../../util/indicationTableUtil";
import {RevaPermission} from "../../util/revaPermission";
import {countLeadingWhitespace} from "../../util/stringUtil";

/**
 * Indication Table with rendered data.
 * @param {object} props
 * @param {RevaSpecOutputColumn[]} props.specColumns columns to render
 * @param {ValueIndicationDataRow[]} props.rows rendered rows
 * @param {boolean} [props.fullsize] flag, if the table should be rendered over the full width (no inforation panel visible)
 * @param {string} props.endpoint indication endpoint
 * @param {Object.<string, boolean>} [props.initiallyExpandedGroups] initially expanded groups
 * @param {Object.<string, AppreciationKeyObj>} [props.appreciations] optional appreciation icons in row group headers
 * @param {boolean} [props.editable=true] flag, if cell values may be edited (pencil icon/overlay); set to false for a read-only table
 * @return {React.ReactNode}
 */
export function IndicationTableWithData({
    specColumns,
    rows,
    fullsize,
    endpoint,
    initiallyExpandedGroups,
    appreciations,
    editable = true,
}) {
    const {t, language} = useRevaTranslation();
    const dispatch = useDispatch();
    const allExpandedGroups = useSelector(selectExpandedGroupsByEndpoint);
    const expandedGroups = allExpandedGroups[endpoint] ?? initiallyExpandedGroups;
    const toastRef = useRef(null);

    useEffect(() => {
        if (endpoint && initiallyExpandedGroups) {
            dispatch(initEndpointExpandedGroups({endpoint, groups: initiallyExpandedGroups}));
        }
    }, [dispatch, endpoint, initiallyExpandedGroups]);

    const toggleGroup = (groupKey) => {
        dispatch(toggleExpandedGroup({endpoint, groupKey}));
    };

    const onValueCopy = async (copiedValue) => {
        if (copiedValue == null) return;

        try {
            await navigator.clipboard.writeText(copiedValue);
            toastRef.current.show({
                severity: "info",
                summary: "Info",
                detail: t("value.valueCopiedToast"),
            });
        } catch (e) {
            console.warn("Could not copy value to clipboard", e);
        }
    };

    const headerTemplate = (data) => {
        const groupKey = data.group.groupKey;
        const group = data.group.title;
        const isExpanded = expandedGroups[groupKey] !== false;
        const appreciation = appreciations?.[groupKey];
        const button = (className) => (
            <CollapsibleTitleButton
                label={group}
                isExpanded={isExpanded}
                onToggle={() => toggleGroup(groupKey)}
                appreciation={appreciation}
                className={`group-button ${className}`}
            />
        );
        if (appreciation) {
            return <div className="relative w-full flex flex-row items-center">{button("grow mr-4")}</div>;
        }
        return button("");
    };

    const className = fullsize ? "" : "pr-6 w-full";

    // A column gets its own dedicated (left-aligned) span column right next to it, but only if some
    // row actually has a low/high range for it — VALUE/COMPARATIVE_VALUE/etc. never do today, so
    // this stays a no-op there. Sized to content (not part of the percentage split below), since a
    // span like "10,00 – 15,00 kg/m²a" doesn't need or benefit from a fixed share of the width.
    const spanColumns = specColumns.filter((col) =>
        rows.some((row) => row[col.field]?.low != null && row[col.field]?.high != null),
    );

    const totalColumns = specColumns.length + 1; // Including title column
    const width = (1 / totalColumns) * 100;
    const columnWidth = width - 1;
    const titleColumnWidth = width + totalColumns - 1;

    return (
        <>
            <DataTable
                id={`indicationTable_${endpoint}`}
                value={rows}
                rowGroupMode="subheader"
                groupRowsBy="group"
                emptyMessage={t("value.emptyMessage")}
                tableClassName="indication-table"
                rowClassName={(data) => rowClassName(data, expandedGroups)}
                rowGroupHeaderTemplate={headerTemplate}
                size="small"
                stripedRows
                className={className}
            >
                <Column
                    field="title"
                    style={{
                        minWidth: "16em",
                        width: `${titleColumnWidth}%`,
                    }}
                    className="title-column"
                    header=""
                    body={(data) => <TitleRowCell value={data} />}
                />
                {specColumns.flatMap((col) => {
                    const valueColumn = (
                        <Column
                            key={col.field}
                            field={col.field}
                            header={selectByLanguage(language, col.headerDe, col.headerEn)}
                            className="value-column p-align-right"
                            headerClassName="p-align-right pr-8"
                            style={{
                                minWidth: "4em",
                                width: `${columnWidth}%`,
                            }}
                            body={(rowData) => {
                                const cellData = rowData[col.field];
                                if (cellData && typeof cellData === "object") {
                                    return (
                                        <EditableCell
                                            cellData={cellData}
                                            onValueCopy={onValueCopy}
                                            editable={editable}
                                        />
                                    );
                                }
                                return cellData;
                            }}
                        />
                    );
                    if (!spanColumns.includes(col)) {
                        return [valueColumn];
                    }
                    const spanColumn = (
                        <Column
                            key={`${col.field}_span`}
                            field={col.field}
                            header={t("value.span")}
                            className="p-align-left"
                            headerClassName="p-align-left"
                            style={{
                                // Shrink-to-fit (not part of the percentage split above): "1%" is
                                // the standard trick to make an auto-layout table column size
                                // itself from its content instead of taking a fixed share.
                                width: "1%",
                                minWidth: "4em",
                                whiteSpace: "nowrap",
                                fontSize: "14px",
                                color: "var(--text-color-secondary)",
                            }}
                            body={(rowData) => {
                                const cellData = rowData[col.field];
                                if (cellData?.low == null || cellData?.high == null) return null;
                                return (
                                    <>
                                        {cellData.low}&nbsp;–&nbsp;{cellData.high}
                                    </>
                                );
                            }}
                        />
                    );
                    return [valueColumn, spanColumn];
                })}
            </DataTable>
            <Toast ref={toastRef} position="top-center" className="text-left" />
        </>
    );
}

/**
 * Get CSS classes for row.
 * @param {ValueIndicationDataRow} data row data
 * @param {Object.<string, boolean>} [expandedGroups] expanded groups
 * @return {string|Object<string,boolean>}
 */
export const rowClassName = (data, expandedGroups) => {
    if (expandedGroups != null && expandedGroups[data.group.groupKey] === false) {
        return "hidden-row";
    }

    if (!data.formats) {
        console.warn("Missing field 'formats' in row:", data);
    }

    return {
        "font-bold": data.formats?.bold,
        "row-overline": data.formats?.lineAbove,
        "row-blankline": data.formats?.blankLineAbove,
        "row-grayed-out": data.formats?.grayedOut,
    };
};

/**
 * Renders the pure value of cell.
 * @param {ValueWithDetails} cellData
 * @param {(e: MouseEvent<T>) => void} onClick
 * @return {React.ReactNode}
 */
function CellValue({cellData, onClick}) {
    const valueRef = useRef(null);
    const [mounted, setMounted] = useState(false);

    // Force re-rendering if the component after mounting, so the tooltip can be applied correctly
    useEffect(() => {
        if (valueRef.current && cellData.tooltip && !mounted) {
            setMounted(true);
        }
    }, [mounted]);

    return (
        <>
            <CellTooltip valueRef={valueRef} cellData={cellData} />
            <span ref={valueRef} data-pr-position="top" onClick={onClick}>
                {cellData.value}
            </span>
        </>
    );
}

function CellTooltip({cellData, valueRef}) {
    const {t} = useTranslation();
    if (cellData.tooltip == null) return null;

    return (
        <Tooltip target={valueRef.current} className="custom-tooltip">
            <div>{cellData.tooltip}</div>
            {cellData.originalValue != null && (
                <>
                    <div>
                        <br />
                        {t("value.nonRoundedValue") + cellData.originalValue}
                    </div>
                    <div>
                        <i>{t("value.actions.copyToClipboard")}</i>
                    </div>
                </>
            )}
        </Tooltip>
    );
}

/**
 * Overlay to edit a value (rendered only if cell has associated input parameter).
 * @param {ValueWithDetails} cellData
 * @param {() => void} onHide
 * @param {RefObject<OverlayPanel>} ref reference to the overlay
 * @return {React.ReactNode|null}
 */
function CellEditOverlay({cellData, onHide, ref}) {
    const ranges = useSelector(selectRangesForApiParamCurried(cellData?.inputApiParam));
    if (!cellData?.inputApiParam) return null;

    let editInput;
    switch (cellData.inputApiParam) {
        case "gross_floor_area":
            editInput = <InputGrossFloorAreaEditPanel />;
            break;
        default:
            editInput = <ParameterInput parameter={cellData.inputApiParam} parameterType="AVM" ranges={ranges} />;
    }
    return (
        <OverlayPanel ref={ref} onHide={onHide} className="bg-surface-100 dark:bg-surface-700">
            {editInput}
        </OverlayPanel>
    );
}

/**
 * Table Cell with edit functionality.
 * @param {ValueWithDetails} cellData
 * @param {(string) => void} onValueCopy
 * @param {boolean} [editable=true] flag, if this cell may be edited at all (forced false for read-only tables)
 * @return {React.ReactNode}
 */
function EditableCell({cellData, onValueCopy, editable = true}) {
    const dispatch = useDispatch();
    const editOverlayRef = useRef(null);
    const editIconRef = useRef(null);
    const persistenceError = useSelector(selectPersistenceErrorForSingleParam(cellData.inputApiParam));

    useEffect(() => {
        if (cellEditable && persistenceError && editOverlayRef.current) {
            editOverlayRef.current.show(null, editIconRef?.current);
        } else if (cellEditable && editOverlayRef.current?.isVisible()) {
            editOverlayRef.current.hide();
        }
    }, [persistenceError, editOverlayRef]);

    const cellEditable = editable && !!cellData.inputApiParam && cellData.value != null;
    const iconClassname = cellEditable ? " cursor-pointer" : " opacity-0";

    const showEditMode = (e) => {
        if (cellEditable && editOverlayRef.current) {
            editOverlayRef.current.toggle(e);
            dispatch(setEditedValueDataGroup(cellData.groupTitle));
        }
    };

    const showEditModeAndCopy = (e) => {
        showEditMode(e);
        onValueCopy(cellData.originalValue);
    };

    const cancelEditMode = () => {
        dispatch(setEditedValueDataGroup(null));
        dispatch(setEditedValueParamErrors(null));
    };

    return (
        <>
            <CellValue cellData={cellData} onClick={showEditModeAndCopy} />
            <i className={`pi pi-pencil ml-2 text-gray-400${iconClassname}`} ref={editIconRef} onClick={showEditMode} />
            <CellEditOverlay cellData={cellData} ref={editOverlayRef} onHide={cancelEditMode} />
        </>
    );
}

function TitleRowCell({value}) {
    const title = value.title;
    const leadingWhiteSpaces = countLeadingWhitespace(title);

    return (
        <span
            style={{
                marginLeft: `${leadingWhiteSpaces}rem`,
                display: "block",
            }}
        >
            {title.trimStart()}
        </span>
    );
}

/**
 * Download-Link auf die Methodenbeschreibung eines Verfahrens (#19432), beschriftet mit dem Namen des
 * Verfahrens statt generisch, weil im Konvergenzwert-Tab zwei davon nebeneinander stehen.
 * @param {import("../../api/api-constants-avm").AvmDocumentedEndpoint} endpoint Verfahren, dessen PDF verlinkt wird
 * @return {React.ReactNode}
 */
function MethodDocumentationLink({endpoint}) {
    const {t} = useTranslation();
    const method = t(`value.info.methodName.${endpoint}`);
    return (
        <AvmDocumentationLink
            fetchDocument={() => fetchIndicationDocumentation(endpoint)}
            label={t("value.info.methodDocu", {method})}
            tooltip={t("value.info.methodDocuTooltip", {method})}
            fallbackFilename={`VALUE_${endpoint}_methodenbeschreibung.pdf`}
            className="mb-auto"
        />
    );
}

/**
 * Info sidebar (or, with a different `className`, an info block in another layout — e.g. below a
 * table instead of beside it).
 * @param {RevaIndicationEndpoint} endpoint indication endpoint whose method documentation is linked (#19432)
 * @param {string[]} infoOutput info items
 * @param {string[]} infoModelOutput model info items
 * @param {string[]} infoHintOutput info hint items
 * @param {string} [className] wrapper classes; defaults to the sidebar layout used next to IndicationTableWithData
 * @return {React.ReactNode|null}
 */
export function InfoData({
    endpoint,
    infoOutput,
    infoModelOutput,
    infoHintOutput,
    className = "col-span-12 xl:col-span-3 rounded-border bg-surface-100 dark:bg-surface-700 p-4 mt-2 flex flex-col justify-between",
}) {
    const {t} = useTranslation();
    // Der Vergleichspreis fließt in den Konvergenzwert ein, hat aber keinen eigenen Tab; seine
    // Methodenbeschreibung hängt deshalb hier mit dran. Ohne die Lizenzrolle antwortet die AVM-API
    // mit 403, dann bleibt der Link weg.
    const hasComparativePricePermission = useCheckPermission(RevaPermission.INDICATION_COMPARATIVE_PRICE);
    const showComparativePriceDocu = endpoint === "VALUE" && hasComparativePricePermission;

    const hasInfo = infoOutput && infoOutput.length > 0;
    const hasModelInfo = infoModelOutput && infoModelOutput.length > 0;
    const hasInfoHint = infoHintOutput && infoHintOutput.length > 0;

    if (!hasInfo && !hasModelInfo && !hasInfoHint) {
        return null;
    }

    function InfoOutput({hasContent, output, title}) {
        if (!hasContent) return null;
        return (
            <div className="pb-4">
                <span className="font-bold text-xl">{title}</span>
                <ul>
                    {output.map((item) => (
                        <li key={item}>{item}</li>
                    ))}
                </ul>
            </div>
        );
    }

    return (
        <div className={className}>
            <div>
                <InfoOutput hasContent={hasInfo} output={infoOutput} title={t("value.info.infoHeader")} />
                <InfoOutput
                    hasContent={hasModelInfo}
                    output={infoModelOutput}
                    title={t("value.info.modelInfoHeader")}
                />
                <InfoOutput hasContent={hasInfoHint} output={infoHintOutput} title={t("value.info.infoHintHeader")} />
            </div>
            <div className="flex flex-wrap justify-end gap-y-2">
                {endpoint && <MethodDocumentationLink endpoint={endpoint} />}
                {showComparativePriceDocu && <MethodDocumentationLink endpoint="COMPARATIVE_PRICE" />}
            </div>
        </div>
    );
}

/**
 * Table showing an indication details result.
 * @param {IndicationParameterDetails} indicationDetails result details for endpoint
 * @return {React.ReactNode|null}
 */
function ComparableDetailsTable({indicationDetails}) {
    const comparableDetailsSpec = useSelector(selectComparableValueSpecComparableDetailsParameter);
    const language = useLanguage();
    const getByLanguage = useSelectByCurrentLanguage();
    const [isExpanded, setIsExpanded] = useState(false);

    const parameter = "comparable_details";
    const comparableValues = indicationDetails?.output[parameter]?.map((value) => ({...value, lang: language}));
    // Die Koordinatengenauigkeit steht als „±“ an der Entfernung, nicht in einer eigenen Spalte (#19461).
    const excluded = ["offer_id", "coordinate_lat", "coordinate_lon", "start_date", COMPARABLE_ACCURACY_PROPERTY];
    const properties = comparableDetailsSpec?.model?.properties;
    const columns = properties?.filter((col) => !excluded.includes(col.property));
    const accuracySpec = properties?.find((col) => col.property === COMPARABLE_ACCURACY_PROPERTY);
    const label = getByLanguage(comparableDetailsSpec?.nameDe, comparableDetailsSpec?.nameEn);
    const getTooltip = (columnData) => {
        const tooltip = getByLanguage(columnData.descriptionDe, columnData.descriptionEn);
        if (columnData.property !== COMPARABLE_DISTANCE_PROPERTY || !accuracySpec) return tooltip;
        return `${tooltip} ± ${getByLanguage(accuracySpec.descriptionDe, accuracySpec.descriptionEn)}`;
    };

    if (!comparableValues?.length || !columns?.length) {
        return null;
    }

    const getValue = (data, columnData) => {
        const {type, categories, property, unitDe, unitEn} = columnData;
        const value = data[property];
        if (value == null) {
            return null;
        }

        if (property === COMPARABLE_DISTANCE_PROPERTY) {
            const unit = unitDe ? getByLanguage(unitDe, unitEn) : "";
            const formatted = formatComparableDistance(value, data[COMPARABLE_ACCURACY_PROPERTY], language, unit);
            return (
                <span className="whitespace-nowrap">
                    {formatted.distance}
                    {formatted.accuracy && (
                        <span className="text-surface-500 dark:text-surface-400 ml-2 text-sm">
                            {formatted.accuracy}
                        </span>
                    )}
                </span>
            );
        }

        switch (type) {
            case "category": {
                const cat = categories?.find((category) => category.categoryNumber === value);
                return getByLanguage(cat?.shortNameDe, cat?.shortNameEn);
            }
            case "boolean":
                return value ? getByLanguage("Ja", "Yes") : getByLanguage("Nein", "No");
            case "date":
                return new Date(data.end_date).getFullYear();
            case "string":
                return value;
            default: {
                const isDouble = type === "double";
                const options = {
                    minimumFractionDigits: isDouble ? 2 : 0,
                    maximumFractionDigits: isDouble ? 2 : 0,
                    useGrouping: true,
                };
                const fmt = new Intl.NumberFormat(language, options);
                return property?.includes("year")
                    ? value
                    : fmt.format(value) + (unitDe ? ` ${getByLanguage(unitDe, unitEn)}` : "");
            }
        }
    };

    return (
        <div className="mr-6 pt-2" style={{backgroundColor: "var(--surface-a)"}}>
            <CollapsibleTitleButton
                label={label}
                isExpanded={isExpanded}
                onToggle={() => setIsExpanded(!isExpanded)}
                className="comparables-toggle-button mb-2 w-full text-left"
            />
            {isExpanded && (
                <DataTable value={comparableValues} stripedRows size="small">
                    {columns.map((col) => (
                        <Column
                            key={col?.property}
                            sortable
                            field={col?.property}
                            header={() => getByLanguage(col?.nameDe, col?.nameEn)}
                            body={(data) => (
                                <CellValue cellData={{value: getValue(data, col), tooltip: getTooltip(col)}} />
                            )}
                        />
                    ))}
                </DataTable>
            )}
        </div>
    );
}

function ExplanationsToValueCalculation() {
    const [isExpanded, setIsExpanded] = useState(false);
    const {t} = useTranslation();
    // #18408: nur mit dem Recht für die kompakte Vereinfachte Wertermittlung, die als einzige diesen Text druckt;
    // bei Zwecken ohne Würdigungen (Research, #19481) entfällt der Abschnitt ebenfalls.
    const visible = useValueExplanationsVisible();

    if (!visible) return null;

    return (
        <div className="mr-6 pt-2" style={{backgroundColor: "var(--surface-a)"}}>
            <CollapsibleTitleButton
                label={t("value.explanations")}
                isExpanded={isExpanded}
                onToggle={() => setIsExpanded(!isExpanded)}
                className="comparables-toggle-button mb-2 w-full text-left"
            />
            {isExpanded && (
                <>
                    <div className="text-surface-500 dark:text-surface-400 mb-1 text-sm">
                        {t("value.explanationsHint")}
                    </div>
                    <AppreciationInputText
                        appreciationKey="appreciation_value_explanations"
                        page="value"
                        section="explanations"
                        showToast={false}
                    />
                </>
            )}
        </div>
    );
}

export function getInfoText(output, language, key) {
    const text = selectByLanguage(language, output[`${key}_de`], output[key]);
    return splitMultilineString(text);
}

/**
 * Table showing an indication details result.
 * @param {RevaIndicationEndpoint} endpoint
 * @param {IndicationParameterDetails} indicationDetails result details for endpoint
 * @param {Object.<string, AppreciationKeyObj>} [appreciations] optional appreciation icons in row group headers
 * @return {React.ReactNode|null}
 */
function IndicationTable({endpoint, indicationDetails, appreciations}) {
    const language = useLanguage();
    const spec = useSelector(selectSpec);

    if (!endpoint || !indicationDetails?.output || !spec?.pages?.[endpoint]) {
        return null;
    }

    const renderedRows = aggregateIndicationTableData(language, spec, endpoint, indicationDetails);

    const infoOutput = getInfoText(indicationDetails.output, language, "info_info");
    const infoModelOutput = getInfoText(indicationDetails.output, language, "info_model");
    const infoHintOutput = getInfoText(indicationDetails.output, language, "info_hint");

    const noSideBar = !infoOutput.length && !infoModelOutput.length;

    const initiallyExpandedGroups = Object.fromEntries(
        renderedRows.map((row) => [row.group.groupKey, row.group.unfoldInitially]),
    );

    return (
        <>
            <ValueLendingValueInfo endpoint={endpoint} />
            <div className="grid grid-cols-12 gap-4 mx-0">
                <div className="col-span-12 xl:col-span-9 pl-0">
                    <IndicationTableWithData
                        rows={renderedRows}
                        specColumns={spec.pages[endpoint].columns}
                        fullsize={noSideBar}
                        endpoint={endpoint}
                        initiallyExpandedGroups={initiallyExpandedGroups}
                        appreciations={appreciations}
                    />
                    {endpoint === "COMPARATIVE_VALUE" && (
                        <ComparableDetailsTable indicationDetails={indicationDetails} />
                    )}
                    {endpoint === "VALUE" && <ExplanationsToValueCalculation />}
                </div>
                <InfoData
                    endpoint={endpoint}
                    infoOutput={infoOutput}
                    infoModelOutput={infoModelOutput}
                    infoHintOutput={infoHintOutput}
                />
            </div>
        </>
    );
}

export default IndicationTable;
